import { readStorageItem, readStorageJSON, removeStorageItem, resolveStoredAuthToken, safeParseJSON, writeStorageJSON } from '../utils/storage';

// Durable cache identity contains no credential, credential digest, or token-derived ID.
export const QUERY_OWNER_STORAGE_KEY = 'ls:query-owner';
const SNAPSHOT_PREFIX = 'ls:offline:';
let owner = null;
let credentials = null;
let initialized = false;
const ownerListeners = new Set();
const invalidationListeners = new Set();
const homeRevisions = new Map();
const homeListeners = new Map();
const idOf = value => typeof value === 'string' && value.trim()
  ? value.trim()
  : (typeof value === 'number' && Number.isFinite(value) ? String(value) : null);
export const actorIdOf = user => idOf(user?._id ?? user?.id ?? user?.userId ?? user?.sub);
export const communityIdOf = value => {
  if (Array.isArray(value)) return value.map(communityIdOf).find(Boolean) || null;
  if (value && typeof value === 'object') {
    return communityIdOf(value.community ?? value.communityId ?? value._id ?? value.id);
  }
  return idOf(value);
};
const validOwner = value => Boolean(value && typeof value === 'object'
  && !Array.isArray(value) && Boolean(idOf(value.actorId)) && idOf(value.actorId) === value.actorId
  && (value.communityId === '' || Boolean(idOf(value.communityId)) && idOf(value.communityId) === value.communityId)
  && typeof value.sessionId === 'string' && /^[a-zA-Z0-9-]{16,128}$/.test(value.sessionId)
  && Object.keys(value).every(key => ['actorId', 'communityId', 'sessionId'].includes(key)));
const nonce = () => {
  if (globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  return Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
};
const storageKeys = () => {
  try {
    return Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index)).filter(Boolean);
  } catch { return []; }
};
const pruneOfflineSnapshots = next => storageKeys().filter(key => key.startsWith(SNAPSHOT_PREFIX))
  .forEach(key => {
    const stored = readStorageJSON(key, { area: 'session', fallback: null });
    if (!next || !validOwner(stored?.owner) || stored.owner.actorId !== next.actorId
      || stored.owner.sessionId !== next.sessionId) removeStorageItem(key, { area: 'session' });
  });
export const queryScopeId = scope => validOwner(scope)
  ? JSON.stringify([scope.actorId, scope.communityId, scope.sessionId]) : 'guest';
export const subscribeQueryOwnerChanges = listener => {
  ownerListeners.add(listener);
  return () => ownerListeners.delete(listener);
};
export const subscribePrivateCacheInvalidation = listener => {
  invalidationListeners.add(listener);
  return () => invalidationListeners.delete(listener);
};
export const invalidatePrivateServiceCaches = ({ owner: requestOwner, credentialScope, path } = {}) => {
  invalidationListeners.forEach(listener => listener({ owner: requestOwner, credentialScope, path }));
};
const publishOwner = next => {
  const previous = owner;
  owner = next;
  if (queryScopeId(previous) !== queryScopeId(next)) {
    pruneOfflineSnapshots(next);
    homeRevisions.clear();
    ownerListeners.forEach(listener => listener(next, previous));
  }
  return owner;
};
export const retireQueryOwner = () => {
  initialized = true;
  credentials = null;
  removeStorageItem(QUERY_OWNER_STORAGE_KEY, { area: 'session' });
  return publishOwner(null);
};
export const syncQueryOwner = ({ token, user, refreshToken } = {}) => {
  const actorId = actorIdOf(user);
  const communityId = communityIdOf(user?.community ?? user?.communityId) || '';
  if (!token || !actorId) return retireQueryOwner();
  if (owner && owner.actorId === actorId && owner.communityId === communityId
    && credentials?.token === token
    && (refreshToken === undefined || credentials?.refreshToken === refreshToken)) return owner;
  const saved = !initialized
    ? readStorageJSON(QUERY_OWNER_STORAGE_KEY, { area: 'session', fallback: null }) : null;
  initialized = true;
  credentials = { token, refreshToken };
  const next = Object.freeze({ actorId, communityId,
    sessionId: validOwner(saved) && saved.actorId === actorId && saved.communityId === communityId
      ? saved.sessionId : nonce() });
  writeStorageJSON(QUERY_OWNER_STORAGE_KEY, next, { area: 'session' });
  return publishOwner(next);
};
export const getActiveQueryOwner = () => {
  if (!initialized) {
    const user = readStorageJSON('authUser', { area: 'session', fallback: null })
      || readStorageJSON('authUser', { fallback: null }) || readStorageJSON('user', { fallback: null });
    const rawRefresh = readStorageItem('authRefreshToken', { fallback: null })
      || readStorageItem('refreshToken', { fallback: null });
    const parsedRefresh = safeParseJSON(rawRefresh, rawRefresh);
    syncQueryOwner({ token: resolveStoredAuthToken(), user,
      refreshToken: typeof parsedRefresh === 'string' ? parsedRefresh : parsedRefresh?.refreshToken });
  }
  return owner;
};
export const isActiveQueryCredential = token => Boolean(owner && token && token === credentials?.token);
export const ownsQueryScope = scope => {
  const active = getActiveQueryOwner();
  return Boolean(scope && validOwner({ actorId: scope.actorId, communityId: scope.communityId, sessionId: scope.sessionId }) && active
    && scope.actorId === active.actorId && scope.sessionId === active.sessionId);
};
export const captureQueryScope = ({ user, token, communityId } = {}) => {
  if (!isActiveQueryCredential(token) || actorIdOf(user) !== owner.actorId) return null;
  const effectiveCommunity = communityId === undefined
    ? owner.communityId : communityIdOf(communityId) || '';
  return Object.freeze({ ...owner, communityId: effectiveCommunity });
};
export const isAccessDenied = value => {
  const status = value?.status ?? value?.response?.status;
  return status === 401 || status === 403;
};

const legacySnapshot = key => /^(previewFeed:|recentComments:|lsa:|communityMeta:|communityStats:|notifications:)/.test(key)
  || key === 'ls:home-bootstrap-meta';
let discardedLegacySnapshots = false;
const discardLegacySnapshots = () => {
  if (discardedLegacySnapshots) return;
  discardedLegacySnapshots = true;
  storageKeys().filter(legacySnapshot).forEach(key => removeStorageItem(key, { area: 'session' }));
};
export const offlineNewsPreview = posts => (Array.isArray(posts) ? posts : []).slice(0, 9)
  .map(post => ({ ...post,
    ...(Array.isArray(post.comments) ? {
      comments: post.comments.slice(-3),
      commentCount: post.commentCount ?? post.totalComments ?? post.comments.length,
    } : {}),
  }));
const snapshotKey = (family, scope) => `${SNAPSHOT_PREFIX}${family}:${queryScopeId(scope)}`;
export const readOfflineSnapshot = (family, scope, fallback = null) => {
  discardLegacySnapshots();
  if (!ownsQueryScope(scope)) return fallback;
  const stored = readStorageJSON(snapshotKey(family, scope), { area: 'session', fallback: null });
  if (!stored || queryScopeId(stored.owner) !== queryScopeId(scope)) return fallback;
  const value = stored.value ?? fallback;
  return family === 'previewFeed' ? offlineNewsPreview(value) : value;
};
export const writeOfflineSnapshot = (family, scope, value) => {
  if (!ownsQueryScope(scope)) return false;
  discardLegacySnapshots();
  const offlineValue = family === 'previewFeed' ? offlineNewsPreview(value) : value;
  return writeStorageJSON(snapshotKey(family, scope), { owner: scope, value: offlineValue }, { area: 'session' });
};
export const removeOfflineSnapshot = (family, scope) => {
  if (validOwner(scope)) removeStorageItem(snapshotKey(family, scope), { area: 'session' });
};
export const clearOfflineSnapshots = () => storageKeys()
  .filter(key => key.startsWith(SNAPSHOT_PREFIX) || legacySnapshot(key))
  .forEach(key => removeStorageItem(key, { area: 'session' }));
export const getHomeProjectionRevision = scope => homeRevisions.get(queryScopeId(scope)) || 0;
export const captureHomeProjectionRequest = scope => {
  const revision = getHomeProjectionRevision(scope);
  // Callers compose this shared projection revision with their lifecycle/owner
  // guard. Keeping this independent also supports standalone hook consumers.
  return () => getHomeProjectionRevision(scope) === revision;
};
export const revokeHomeProjectionRequests = scope => {
  const id = queryScopeId(scope);
  homeRevisions.set(id, getHomeProjectionRevision(scope) + 1);
  homeListeners.get(id)?.forEach(listener => listener());
};
export const subscribeHomeProjectionRequests = (scope, listener) => {
  const id = queryScopeId(scope);
  if (!homeListeners.has(id)) homeListeners.set(id, new Set());
  const listeners = homeListeners.get(id);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) homeListeners.delete(id);
  };
};
